import type { ReactNode } from 'react';
import { InlineStack } from '@customerio/pluma-components/react';
import { fieldError, type BuilderError } from '@customerio/dryad';
import { useBuilderActions, useLeafState, useNodeErrors } from '@customerio/dryad-react';
import type { AttributeFilterState } from '../../attribute-filter-state';
import type { OperatorGroup } from '../../operator-groups';
import { OperatorValue } from '../operator-value/operator-value';
import type { OperatorValuePatch, ValueOption } from '../operator-value/types';

/**
 * What the shared attribute row hands its name picker. The picker is per
 * surface: people searches its profile-attribute endpoint, collections offers
 * a static schema with a create option, and a third surface may do neither.
 */
export type AttributeNamePickerProps = {
  /** The attribute currently named, or '' when the row is fresh. */
  field: string;
  /** Message for a field-tagged error, once the picker has been visited. */
  error?: string;
  isDisabled: boolean;
  /** Commits a picked attribute name. */
  onFieldChange: (field: string) => void;
  /** Marks the picker visited, so its error can surface. */
  onTouched: () => void;
};

export type AttributeRowProps = {
  /** The leaf node's id, supplied by the group to every registered view. */
  id: string;
  /** The plugin type gating the typed read — the surface's attribute leaf. */
  conditionType: string;
  /** The operator dropdown, derived from the surface's declared families. */
  operatorGroups: (field: string) => OperatorGroup[];
  /** The attribute-name control, owned by the surface that fetches its options. */
  renderNamePicker: (props: AttributeNamePickerProps) => ReactNode;
  /** The condition-type select, where the surface offers more than one type. */
  typeSelect?: ReactNode;
  isDisabled: boolean;
  /** Autosuggest entries for the value input; empty where none exist. */
  valueOptions: ValueOption[];
  isLoadingValueOptions?: boolean;
  /** Marks the name picker visited; the surface owns the state. */
  isFieldTouched: boolean;
  onFieldTouched: () => void;
  /**
   * Cross-axis alignment for the row. People aligns on the baseline so its
   * inline field errors don't shift the controls; collections centres, which
   * is what its rows looked like before they shared this component.
   */
  alignBlock?: 'baseline' | 'center';
};

/**
 * The attribute condition row: [type] [attribute name] [operator] [value…].
 *
 * The skeleton and the operator/value half are shared; the attribute-name
 * picker is not, because where the names come from is the one thing that
 * genuinely differs between surfaces. Ember's equivalent is its most-reused
 * row (11 call sites) for the same reason.
 */
export function AttributeRow({
  id,
  conditionType,
  operatorGroups,
  renderNamePicker,
  typeSelect,
  isDisabled,
  valueOptions,
  isLoadingValueOptions,
  isFieldTouched,
  onFieldTouched,
  alignBlock = 'baseline',
}: AttributeRowProps) {
  const { state, node } = useLeafState<AttributeFilterState>(id, conditionType);
  const { updateState } = useBuilderActions();
  const errors: BuilderError[] = useNodeErrors(id);

  const fieldErrorMessage = isFieldTouched ? fieldError(errors, 'field') : undefined;

  const patch = (next: OperatorValuePatch | Partial<AttributeFilterState>) => {
    if (node != null) {
      updateState(node, next);
    }
  };

  return (
    <InlineStack data-testid="dryad-condition-row" gap="050" alignBlock={alignBlock} wrap>
      {typeSelect}
      {renderNamePicker({
        field: state.field,
        error: fieldErrorMessage,
        isDisabled,
        onFieldChange: (field) => patch({ field }),
        onTouched: onFieldTouched,
      })}
      <OperatorValue
        field={state.field}
        operator={state.operator}
        value={state.value}
        value2={state.value2}
        valueOptions={valueOptions}
        isLoadingValueOptions={isLoadingValueOptions}
        operatorGroups={operatorGroups(state.field)}
        disabled={isDisabled}
        errors={errors}
        onChange={patch}
      />
    </InlineStack>
  );
}
